import { Subscribe } from '@react-rxjs/core'
import React, { useEffect } from 'react'

import { Maybe } from '../../../graphql/gatsby'
import { useBlockedUserToast } from '../../../lib/auth/hooks/useBlockedUserToast'
import { usePatientBrowseDispensaryToast } from '../../../lib/dispensaries/hooks/usePatientBrowseDispensaryToast'
import { HeaderStyle, headerStyle$, setHeaderStyleBase } from '../../../lib/header/style'
import { useUserPrivacyQualifiers } from '../../../lib/privacy/hooks/useUserPrivacyQualifiers'
import DisabledSignupModal from '../../auth/DisabledSignupModal'
import { SessionExpirationModal } from '../../auth/SessionExpirationModal'
import { HideAllToasts } from '../../HideAllToasts'
import AccountSetupErrorModal from '../../onboarding/AccountSetupErrorModal'
import { SubmitPractitionerFormsReminder } from '../../onboarding/practitioner/SubmitPractitionerFormsReminder'
import { QuickViewProductModal } from '../../products/QuickViewProductModal'
import { SupportTicketModal } from '../../support/SupportTicketModal'
import { cn } from '../../utils/cn'
import { Footer } from '../footer'
import { Header } from '../header'
import * as Styled from './styled'

import arrowDownIcon from '../../../img/icons/arrow-down.svg'

interface PageContainerProps {
  children?: React.ReactNode
  className?: string
  $fillHeight?: boolean
  $fullWidth?: boolean
  $increasedWidth?: boolean
}

const PageContainer: React.FC<PageContainerProps> = ({
  children,
  className,
  $fillHeight,
  $fullWidth,
  $increasedWidth,
}) => (
  <div
    id="mainContent"
    className={cn(
      'w-full relative mx-auto bg-no-repeat min-w-[320px]',
      !$fullWidth && ($increasedWidth ? 'max-w-[1440px]' : 'max-w-[1280px]'),
      $fillHeight ? 'h-full' : '',
      className,
    )}
    data-cy="page-container"
  >
    {children}
  </div>
)

const Toasts: React.FC = () => {
  usePatientBrowseDispensaryToast()
  useBlockedUserToast()

  return <SubmitPractitionerFormsReminder />
}

const hiddenLinkStyle =
  'relative px-4 py-2 inline-flex items-center focus:outline-none focus-visible:underline focus-visible:ring-0'

export interface LayoutProps {
  children?: React.ReactNode
  className?: string
  fullWidth?: boolean
  increasedWidth?: boolean
  noToasts?: boolean
  showFooter?: boolean
  showHeader?: boolean
  headerStyle?: Maybe<HeaderStyle>
}

const Layout: React.FC<LayoutProps> = ({
  children,
  className,
  fullWidth = false,
  increasedWidth = true,
  noToasts = false,
  showFooter = true,
  showHeader = true,
}) => {
  useUserPrivacyQualifiers()

  return (
    <Styled.Container
      $enableHeaderPadding={showHeader}
      $fillHeight={!showFooter}
      $fullWidth={fullWidth}
      data-cy="layout-root"
    >
      <div
        className={cn(
          'absolute z-[10000] left-4 -top-full transition-all duration-150',
          'flex flex-col bg-white border-brand-orange border rounded-xl divide-y divide-brand-orange',
          'overflow-hidden shadow-md',
          'focus-within:top-4',
        )}
      >
        <a href="#mainContent" className={cn(hiddenLinkStyle, 'focus-visible:rounded-t-xl')}>
          <img className="mr-2 w-4 h-4" src={arrowDownIcon} alt="" aria-hidden="true" />
          Skip To Main Content
        </a>
      </div>
      {showHeader && <Header />}
      <PageContainer
        className={className}
        $fillHeight={!showFooter}
        $fullWidth={fullWidth}
        $increasedWidth={increasedWidth}
      >
        {children}
      </PageContainer>
      {showFooter && <Footer />}
      {noToasts ? <HideAllToasts /> : <Toasts />}
      <SessionExpirationModal />
      <QuickViewProductModal />
      <SupportTicketModal />
      <AccountSetupErrorModal />
      <DisabledSignupModal />
    </Styled.Container>
  )
}

const LayoutWrapper: React.FC<LayoutProps> = (props) => {
  useEffect(() => {
    setHeaderStyleBase(props.headerStyle ?? 'white')
  }, [props.headerStyle])

  return (
    <Subscribe source$={headerStyle$} fallback={null}>
      <Layout {...props} />
    </Subscribe>
  )
}

export default LayoutWrapper
